import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["field"]

  clearErrors() {
    this.element.querySelectorAll(".form-validation-error").forEach((field) => {
      field.classList.remove("form-validation-error")
    })

    this.element.querySelectorAll(".form-validation-error-message").forEach((message) => {
      message.remove()
    })
  }

  clearFieldError(field) {
    field.classList.remove("form-validation-error")

    const errorMessageId = this.getErrorMessageId(field)
    const errorMessage = document.getElementById(errorMessageId)

    if (errorMessage) {
      errorMessage.remove()
    }
  }

  connect() {
    this.element.addEventListener("submit", this.validateForm)
    // Disable HTML5 validation to prevent conflicts with hidden fields
    this.element.noValidate = true

    this.fieldTargets.forEach((field) => {
      field.addEventListener("change", this.validateField)
      field.addEventListener("blur", this.validateField)
    })
  }

  createErrorMessage(field) {
    const errorDiv = document.createElement("div")

    errorDiv.id = this.getErrorMessageId(field)
    errorDiv.classList.add(
      "font-bold",
      "form-validation-error-message",
      "mt-1",
      "text-red-600",
      "text-xs"
    )

    const fieldName = this.getFieldDisplayName(field)
    errorDiv.textContent = `${fieldName} is required`

    return errorDiv
  }

  disconnect() {
    this.element.removeEventListener("submit", this.validateForm)

    // Remove change listeners from all fields
    this.fieldTargets.forEach((field) => {
      field.removeEventListener("change", this.validateField)
      field.removeEventListener("blur", this.validateField)
    })
  }

  fieldTargetConnected(field) {
    field.addEventListener("change", this.validateField)
    field.addEventListener("blur", this.validateField)
  }

  fieldTargetDisconnected(field) {
    field.removeEventListener("change", this.validateField)
    field.removeEventListener("blur", this.validateField)
  }

  findInsertionPoint(field) {
    if (field.type === "radio") {
      const radioContainer = field.closest(".flex")?.parentElement || field.parentElement
      return radioContainer
    }

    const fieldContainer =
      field.closest(".field") ||
      field.closest(".form-group") ||
      field.closest("div") ||
      field.parentElement

    return fieldContainer
  }

  getErrorMessageId(field) {
    let fieldId = field.id || field.name

    // If no id or name, assign a stable one to the field itself
    if (!fieldId) {
      if (!field.dataset.formValidationId) {
        field.dataset.formValidationId = `field-${Math.random().toString(36).substr(2, 9)}`
      }
      fieldId = field.dataset.formValidationId
    }

    return `form-validation-error-${fieldId}`
  }

  getFieldDisplayName(field) {
    const label =
      field.dataset.fieldName ||
      field.getAttribute("aria-label") ||
      field.placeholder ||
      field.name?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) ||
      "This field"

    return label
  }

  isFieldEmpty(field) {
    const value = field.value?.trim()

    if (field.type === "radio") {
      const radioGroup = this.element.querySelectorAll(`input[name="${field.name}"]`)
      return !Array.from(radioGroup).some((radio) => radio.checked)
    }

    if (field.type === "checkbox") {
      return !field.checked
    }

    return !value || value === ""
  }

  isFieldRequired(field) {
    return (
      field.hasAttribute("required") ||
      field.dataset.required === "true" ||
      field.classList.contains("required")
    )
  }

  showError(field) {
    field.classList.add("form-validation-error")

    const errorMessage = this.createErrorMessage(field)

    const insertionPoint = this.findInsertionPoint(field)
    insertionPoint.insertAdjacentElement("afterend", errorMessage)
  }

  validateField = (event) => {
    const field = event.target

    // For radio buttons, clear all errors for the entire group
    if (field.type === "radio") {
      const radioGroup = this.element.querySelectorAll(`input[name="${field.name}"]`)
      radioGroup.forEach((radio) => this.clearFieldError(radio))
    } else {
      this.clearFieldError(field)
    }

    if (this.isFieldRequired(field) && this.isFieldEmpty(field)) {
      this.showError(field)
    }
  }

  validateForm = (event) => {
    let hasErrors = false

    this.clearErrors()

    // Group fields by radio button groups to avoid duplicate errors
    const processedRadioGroups = new Set()

    this.fieldTargets.forEach((field) => {
      if (this.isFieldRequired(field) && this.isFieldEmpty(field)) {
        // For radio buttons, only show one error per group
        if (field.type === "radio") {
          const radioGroupKey = field.name
          if (processedRadioGroups.has(radioGroupKey)) {
            return // Skip this radio button, we already processed this group
          }
          processedRadioGroups.add(radioGroupKey)
        }

        this.showError(field)
        hasErrors = true
      }
    })

    if (hasErrors) {
      event.preventDefault()
      event.stopPropagation()

      const firstErrorField = this.element.querySelector(".form-validation-error")

      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }
  }
}
